import React, { forwardRef } from 'react'
import { TradingEntity, Organisation } from 'api'
import styled from '@emotion/styled'
import { MenuWrapper } from 'view/components/menus'
import { MultiComboboxProps, MultiCombobox } from 'view/components/options'
import * as constants from 'view/styles/constants'
import { Checkbox } from 'view/components/inputs'

export interface OrganisationsComboboxProps extends MultiComboboxProps<TradingEntity, string> {
  organisations: Organisation[]
  selectedLabel: string
}

const DropDownContainer = styled.div`
  min-width: 250px;
`
const OrganisationsComboboxContainer = styled.div`
  max-width: 250px;
  width: 100%;
  ${MenuWrapper} {
    width: 100%;
  }
`

const SelectedCounterparty = styled.span`
  cursor: inherit;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: calc(100% - 24px);
  height: 24px;
  background-color: ${constants.CORE_PRIMARY};
  border-radius: 6px;
  border: none;
  outline: 0;
  padding-left: 6px;
  padding-right: 4px;
  span {
    overflow: hidden;
    width: calc(100% - 20px);
  }
`

export const OrganisationsCombobox: React.FC<OrganisationsComboboxProps> = forwardRef((props, ref: React.Ref<MultiCombobox>) => {
  const { organisations, selectedLabel, value, onUpdate } = props
  const optionKeys = organisations.map(org => org.id)
  const allSelected = value.length === optionKeys.length
  const partialSelected = value.length > 0 && value.length < optionKeys.length

  return (
    <OrganisationsComboboxContainer>
      <MultiCombobox
        {...props}
        ref={ref}
        optionKeys={optionKeys}
        getOption={(id: string) => organisations.find(org => org.id === id)?.legalName}
        showSelectedOptionsPane={false}
        onOptionClick={(_k, _v, e) => {
          e.stopPropagation()
          e.preventDefault()
        }}
        innerComboboxProps={{
          onOptionSelected: (o: string) => onUpdate(value.includes(o) ? value.filter(v => v !== o) : [...value, o]),
          inputDecorator: () => (
            <SelectedCounterparty tabIndex={0}>
              <span data-test-id={'Combobox-SelectedItem'}>{selectedLabel}</span>
              <Checkbox
                isChecked={allSelected}
                isPartialChecked={partialSelected}
                onUpdate={() => onUpdate(allSelected || partialSelected ? [] : [...optionKeys])}
              />
            </SelectedCounterparty>
          ),
          contentDecorator: (content: JSX.Element) => <DropDownContainer>{content}</DropDownContainer>
        }}
      />
    </OrganisationsComboboxContainer>
  )
})
